/**
 * reviewFinalize.ts
 *
 * Persists a Title Search review through the existing `finalizeDocumentReview`
 * mutation, one call per source file, with the same payload shape the legacy
 * `RecordedDocumentReviewStep` wrote (`{ [documentId]: [{ field, value,
 * confidence, page }] }`):
 *
 * - an edited field carries its corrected value;
 * - a rejected field is kept with `action: 'REJECT'` (and its extracted value)
 *   so a reopened review can show it. Every consumer of the stored list skips
 *   `REJECT` entries (`isRejectedEntry` in `titleSearch/timeline.ts`, the Title
 *   analysis input assembly in the backend), so a rejected value is never read.
 *
 * Each field also carries `action` (`ACCEPT` / `EDIT` / `REJECT`, upper case as
 * the API stores it), `extracted_value` (the value as extracted, for an edited
 * field's "Extracted: …" reference) and `bounding_box`, so a reopened review
 * can show the stored action and edited value. Consumers that read only
 * `field` / `value` / `page` ignore the extra keys.
 *
 * Requirements: 8.6, 8.8.
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { graphqlClient } from '../../services/graphqlClient';
import * as workflowService from '../../services/workflowService';
import type { File as FileType, WorkflowJob } from '../../graphql/types';
import type { BoundingBox } from '../../components/documentViewerGeometry';
import type { FieldAction } from '../../titleSearch/review';
import { displayValue } from './reviewModel';
import type { ReviewActions, ReviewDocument } from './reviewModel';

export type StoredReviewAction = 'ACCEPT' | 'EDIT' | 'REJECT';

const STORED_ACTION: Record<FieldAction, StoredReviewAction> = {
  accept: 'ACCEPT',
  edit: 'EDIT',
  reject: 'REJECT',
};

export interface FinalizedFieldEntry {
  field: string;
  value: string;
  confidence: number;
  page: number;
  action?: StoredReviewAction;
  /** The value as extracted; set for every field with an action. */
  extracted_value?: string;
  bounding_box?: BoundingBox;
}

/** `{ [documentId]: fields }` for one file. */
export type FilePayload = Record<string, FinalizedFieldEntry[]>;

/** Payload per source file id. */
export type ReviewPayloads = Record<string, FilePayload>;

/** Group the reviewer's work by source file, in the shape `finalizeDocumentReview` stores. */
export function buildReviewPayloads(
  documents: ReadonlyArray<ReviewDocument>,
  actions: ReviewActions
): ReviewPayloads {
  const payloads: ReviewPayloads = {};
  for (const doc of documents) {
    const file = (payloads[doc.fileId] ??= {});
    file[doc.documentId] = doc.fields.map((f) => {
      const entry = actions[f.id];
      const out: FinalizedFieldEntry = {
        field: f.name,
        value: displayValue(f, actions),
        confidence: f.confidence,
        page: f.page,
      };
      if (entry) {
        out.action = STORED_ACTION[entry.action];
        out.extracted_value = f.value;
      }
      if (f.boundingBox) out.bounding_box = f.boundingBox;
      return out;
    });
  }
  return payloads;
}

export const FINALIZE_ERROR_MESSAGE =
  'The review could not be saved. Your actions are still here. Check your connection and try Finalize review again.';

export const FINALIZE_PENDING_MESSAGE =
  'The review was saved, but the search has not moved on yet. Reload the page to continue.';

/**
 * Save every file's reviewed fields, then fetch the job again so the advanced
 * step status (which unlocks Findings and Title analysis) is available.
 * Rejects when any save fails; the caller keeps the reviewer's actions.
 */
export async function persistReview(args: {
  jobId: string;
  documents: ReadonlyArray<ReviewDocument>;
  files: Record<string, Pick<FileType, 'fileId' | 'loanId' | 'jobId'>>;
  actions: ReviewActions;
}): Promise<WorkflowJob | null> {
  const { jobId, documents, files, actions } = args;
  const session = await fetchAuthSession();
  const tenantId =
    (session.tokens?.idToken?.payload['custom:tenantId'] as string) || 'default-tenant';

  const payloads = buildReviewPayloads(documents, actions);
  for (const [fileId, payload] of Object.entries(payloads)) {
    const file = files[fileId];
    if (!file) throw new Error(`Source file ${fileId} is not loaded`);
    await graphqlClient.mutations.finalizeDocumentReview(
      file.fileId,
      file.loanId,
      file.jobId,
      tenantId,
      JSON.stringify(payload)
    );
  }
  return workflowService.getJob(jobId, tenantId);
}


/**
 * Save a correction made after the review was finalized. Writes only the edited
 * document's fields (the finalize Lambda updates the documents named in the
 * payload), with the same shape as `persistReview`: the corrected value, the
 * action stored with the review, and the extracted value for reference.
 * Rejects when the save fails.
 */
export async function persistFieldEdit(args: {
  document: ReviewDocument;
  file: Pick<FileType, 'fileId' | 'loanId' | 'jobId'>;
  /** The stored actions, including the edit being saved. */
  actions: ReviewActions;
}): Promise<void> {
  const { document, file, actions } = args;
  const session = await fetchAuthSession();
  const tenantId =
    (session.tokens?.idToken?.payload['custom:tenantId'] as string) || 'default-tenant';
  const payload = buildReviewPayloads([document], actions)[document.fileId];
  await graphqlClient.mutations.finalizeDocumentReview(
    file.fileId,
    file.loanId,
    file.jobId,
    tenantId,
    JSON.stringify(payload)
  );
}
